import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { X } from 'lucide-react';
import PeCommentsSection from './PeCommentsSection';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useEffect, useState } from 'react';
import { CommentResponse, useWorkOrderCommentPost, workOrderCommentGet } from '@brainvaulttech/dgr-react-sdk';
import { displayError } from '@/lib/error';
import { EMOJI_REGEX, WORK_ORDER_COMMENT_PAGE_SIZE } from '@/utils/constant';

const PeCommentsDialog = ({
  workOrderId,
  onCloseClick,
}: {
  workOrderId: string;
  onCloseClick: (newCommentsCount: number) => void;
}) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [comment, setComment] = useState<string>('');
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const { mutate: addComment, status: addCommentStatus } = useWorkOrderCommentPost();
  const [totalComments, setTotalComments] = useState(0);
  const [commentsData, setCommentsData] = useState<CommentResponse[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        setIsFetching(true);
        const response = await workOrderCommentGet(workOrderId, {
          pageNo: page,
          pageSize: WORK_ORDER_COMMENT_PAGE_SIZE,
          sortDirection: 'desc',
          sortField: 'createdAt',
        });
        const newComments = response?.data.comments ?? [];
        const total = response?.data.totalRows ?? 0;
        setTotalComments(total);
        setCommentsData((prev) => {
          const updatedComments = [...newComments.reverse(), ...prev];
          if (updatedComments.length >= total && total > 0) {
            setHasMore(false);
          }
          return updatedComments;
        });
      } catch (e) {
        setHasMore(false);
      } finally {
        setIsFetching(false);
      }
    };

    if (hasMore && !isFetching) {
      void fetchComments();
    }
  }, [page]);

  const saveHandler = () => {
    addComment(
      {
        id: workOrderId,
        data: {
          comment: comment,
        },
      },
      {
        onSuccess: (data) => {
          setComment('');
          setTotalComments((prev) => prev + 1);
          setCommentsData((prev) => [...prev, data.data]);
          setRefreshKey((prev) => prev + 1);
        },
        onError: (e) => {
          displayError(e, 5000);
        },
      }
    );
  };
  return (
    <Sheet modal={false} open={true}>
      <SheetContent
        hideCloseButton={true}
        className="w-[80vw] md:w-[500px] scrollbar-hide bg-white flex flex-col gap-0"
        side="right"
        tabIndex={-1}
      >
        <SheetHeader className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
          <SheetTitle className={`flex flex-col gap-4 bg-white  pb-0`}>
            <div className="flex flex-row justify-between">
              <span className="font-semibold text-xl">Comments</span>
              <div className="flex flex-row gap-2 h-[32px] [&_div]:border-[1px] [&_div]:p-[6px] [&_div]:rounded-md [&_div]:justify-center">
                <div onClick={() => onCloseClick(totalComments)}>
                  <X color="#676767" className="h-[20px] w-[20px] cursor-pointer" />
                </div>
              </div>
            </div>
          </SheetTitle>
        </SheetHeader>
        <div className="flex flex-col h-full justify-between">
          <div className="py-2 h-full" key={refreshKey}>
            <PeCommentsSection
              comments={commentsData}
              setPage={setPage}
              hasMore={hasMore}
              isFetching={isFetching}
              page={page}
            />
          </div>
          <div className="sticky bottom-0 p-2 bg-white">
            <div className="flex flex-row justify-between border-[1px] items-center border-bordercolor rounded-md gap-2 p-2 h-[64px]">
              <Textarea
                textAreaClassName="resize-none h-10 border-[0px] pt-[10px] px-2"
                className="border-none w-full p-0"
                onChange={(e) => {
                  const filteredValue = e.target.value.replace(EMOJI_REGEX, '');
                  e.target.value = filteredValue;
                  setComment(filteredValue.replace(/^\s+/, ''));
                }}
                value={comment}
                placeholder="Type your comment..."
              />
              <Button
                variant="outline"
                className="text-[#fff] h-[36px] w-[103px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
                disabled={!comment || addCommentStatus === 'pending'}
                isLoading={addCommentStatus === 'pending'}
                onClick={saveHandler}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PeCommentsDialog;
